import { Languages } from 'lucide-react';
import type { Lang } from '../../../shared/dto/auth';
import { isNetworkFailure } from '../../api/client';
import { useUpdateProfile } from '../../api/auth';
import { useToast } from '../../components/toast';
import { useI18n } from '../../i18n';
import { errorText } from './errorText';
import { OptionCard, SettingsSection } from './parts';

const LANGS: { value: Lang; key: 'settings.lang_is' | 'settings.lang_en' }[] = [
  { value: 'is', key: 'settings.lang_is' },
  { value: 'en', key: 'settings.lang_en' },
];

/**
 * The language lives on the server profile (AuthProvider applies `me.lang` on every start), so a
 * change only counts once the server has it. Offline the change is refused; if the save fails the
 * previous language is restored, instead of silently switching back on the next launch.
 */
export function LanguageSection() {
  const { lang, setLang, t } = useI18n();
  const toast = useToast();
  const update = useUpdateProfile();

  const choose = async (l: Lang) => {
    if (l === lang || update.isPending) return;
    const previous = lang;
    // `t` from this render speaks the previous language, which is what is shown after a rollback.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      toast.show(t('settings.lang_needs_online'), { tone: 'error' });
      return;
    }
    setLang(l); // switch immediately; confirmed by the server below
    try {
      await update.mutateAsync({ lang: l });
    } catch (e) {
      setLang(previous);
      toast.show(isNetworkFailure(e) ? t('settings.lang_needs_online') : errorText(t, e), { tone: 'error' });
    }
  };

  return (
    <SettingsSection icon={Languages} title={t('settings.language')} id="settings-language">
      <div role="radiogroup" aria-label={t('settings.language')} className="grid gap-3 p-5 sm:grid-cols-2">
        {LANGS.map((o) => (
          <OptionCard key={o.value} selected={lang === o.value} onSelect={() => void choose(o.value)} title={t(o.key)} />
        ))}
      </div>
    </SettingsSection>
  );
}
